import { ToggleItem } from '@finn/design-system/atoms/toggle';
import { ToggleModule } from '@finn/design-system/modules/ToggleModule';
import { FALLBACK_ADDITIONAL_MILEAGE_FEE } from '@finn/ua-constants';
import {
  getFormattedNumber,
  getFormattedPrice,
  twoDecimalPlaces,
  useIntl,
} from '@finn/ui-utils';
import get from 'lodash/get';
import { useCallback } from 'react';

import { getForBusinessCookie } from '../../../helpers';
import { useDiscoveryConfig } from '../../core/data/MetadataProvider';
import { PDPTracking, useFormattedNumber, useTrackingData } from '../../pdp';
import { useConfigureStore } from '../../pdp-management';
import { calculateTotalPrice, getDistancePrices } from '../../product-pricing';

type Price = { distance: number; price: number; duration: number };

export const DistanceOptions = () => {
  const i18n = useIntl();
  const isForBusiness = getForBusinessCookie();
  const {
    vehicle,
    setKilometerPackage,
    kilometerPackage,
    term,
    downPayment: isDownPayment,
  } = useConfigureStore();

  const { priceType, forceRegularPrice } = useDiscoveryConfig();

  const vehiclePrice = get(
    vehicle,
    forceRegularPrice ? 'price' : 'downpayment_prices',
    {}
  ) as Record<string, number>;

  const distancePrices: Price[] = getDistancePrices(
    vehiclePrice,
    isForBusiness,
    priceType ?? '',
    term,
    forceRegularPrice ?? false
  );

  const unitPrice = get(
    vehicle,
    'price.extra_km_price',
    FALLBACK_ADDITIONAL_MILEAGE_FEE
  );

  const extraUnitPrice = useFormattedNumber(
    twoDecimalPlaces(priceType === 'cents' ? unitPrice / 100 : unitPrice)
  );

  const distanceUnit = i18n.formatMessage('general.distanceUnit');

  const title = i18n.formatMessage('pdp.mileagePerMonth');

  const disclaimer = i18n.formatMessage(
    forceRegularPrice
      ? `pdp.mileagePackageDisclaimer.b2b`
      : `pdp.mileagePackageDisclaimer.b2c`,
    { extraUnitPrice }
  );

  const disclaimerTooltip = i18n.formatMessage(
    'pdp.mileagePackageDisclaimerTooltip'
  );

  const trackingData = useTrackingData();
  const trackDistanceClicked = useCallback(
    (newDistance: number) => {
      const newPrice = calculateTotalPrice({
        vehicle,
        term,
        kilometerPackage: newDistance,
        isDownPayment,
      });

      PDPTracking.distanceClicked({
        ...trackingData,
        distancepackage: newDistance,
        price: newPrice,
      });
    },
    [trackingData, vehicle, term, isDownPayment]
  );

  return (
    <div className="pb-6">
      <ToggleModule
        className="mt-1"
        cols={2}
        title={title}
        titleAs={'h6'}
        defaultValue={String(kilometerPackage)}
        value={String(kilometerPackage)}
        onValueChange={(value) => {
          if (value) {
            const newValue = Number(value);
            setKilometerPackage(newValue);
            trackDistanceClicked(newValue);
          }
        }}
        subtitle={`${disclaimer} ${disclaimerTooltip}`}
      >
        {distancePrices.map((option) => {
          const description =
            option.price > 0
              ? `+ ${getFormattedPrice(i18n, option.price, 0)}`
              : i18n.formatMessage('pdp.free');

          return (
            <ToggleItem
              key={option.distance}
              variant="regular"
              title={`${getFormattedNumber(
                i18n,
                option.distance
              )} ${distanceUnit}`}
              value={String(option.distance)}
              description={description}
            />
          );
        })}
      </ToggleModule>
    </div>
  );
};
